package dev.lingvo.shared.screenshots.render

import dev.lingvo.shared.screenshots.geom.Rect

/** Builds a [Path] from canvas-style calls. */
class PathBuilder {
    private val ops = ArrayList<PathOp>()

    fun moveTo(x: Double, y: Double): PathBuilder = apply { ops.add(PathOp.Move(x, y)) }

    fun lineTo(x: Double, y: Double): PathBuilder = apply { ops.add(PathOp.Line(x, y)) }

    fun cubicTo(c1x: Double, c1y: Double, c2x: Double, c2y: Double, x: Double, y: Double): PathBuilder =
        apply { ops.add(PathOp.Cubic(c1x, c1y, c2x, c2y, x, y)) }

    fun close(): PathBuilder = apply { ops.add(PathOp.Close) }

    fun build(): Path = Path(ops.toList())
}

object Paths {
    /** Control-point distance of a quarter circle as one cubic, as a fraction of the radius. */
    const val KAPPA = 0.5522847498

    fun rect(r: Rect): Path = PathBuilder()
        .moveTo(r.x, r.y)
        .lineTo(r.right, r.y)
        .lineTo(r.right, r.bottom)
        .lineTo(r.x, r.bottom)
        .close()
        .build()

    /** Keeps corner curvature within the rectangle. */
    fun roundedRect(r: Rect, radius: Double): Path {
        val c = radius.coerceIn(0.0, minOf(r.w, r.h) / 2)
        if (c == 0.0) return rect(r)
        val k = c * KAPPA
        return PathBuilder()
            .moveTo(r.x + c, r.y)
            .lineTo(r.right - c, r.y)
            .cubicTo(r.right - c + k, r.y, r.right, r.y + c - k, r.right, r.y + c)
            .lineTo(r.right, r.bottom - c)
            .cubicTo(r.right, r.bottom - c + k, r.right - c + k, r.bottom, r.right - c, r.bottom)
            .lineTo(r.x + c, r.bottom)
            .cubicTo(r.x + c - k, r.bottom, r.x, r.bottom - c + k, r.x, r.bottom - c)
            .lineTo(r.x, r.y + c)
            .cubicTo(r.x, r.y + c - k, r.x + c - k, r.y, r.x + c, r.y)
            .close()
            .build()
    }

    /** The ellipse inscribed in [r] as four cubics, clockwise from the top. */
    fun ellipse(r: Rect): Path {
        val rx = r.w / 2
        val ry = r.h / 2
        val cx = r.x + rx
        val cy = r.y + ry
        val kx = rx * KAPPA
        val ky = ry * KAPPA
        return PathBuilder()
            .moveTo(cx, r.y)
            .cubicTo(cx + kx, r.y, r.right, cy - ky, r.right, cy)
            .cubicTo(r.right, cy + ky, cx + kx, r.bottom, cx, r.bottom)
            .cubicTo(cx - kx, r.bottom, r.x, cy + ky, r.x, cy)
            .cubicTo(r.x, cy - ky, cx - kx, r.y, cx, r.y)
            .close()
            .build()
    }
}
